/*
 * Copyright (c) 2022 Certusoft, Inc. All Rights Reserved.
 *
 * This SOURCE CODE FILE, which has been provided by Certusoft as part
 * of a Certusoft product for use ONLY by licensed users of the product,
 * includes CONFIDENTIAL and PROPRIETARY information of Certusoft.
 *
 * USE OF THIS SOFTWARE IS GOVERNED BY THE TERMS AND CONDITIONS
 * OF THE LICENSE STATEMENT AND LIMITED WARRANTY FURNISHED WITH
 * THE PRODUCT.
 */

import { Component, OnInit, QueryList, ViewChildren, ViewEncapsulation } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser'
import * as _ from 'lodash'
import { MatrixFunctions } from '../../models/matrix-functions'
import * as math from 'mathjs'
import { resolveReferences } from '../../../JsonReferenceResolver'
import { NgbPanelChangeEvent } from '@ng-bootstrap/ng-bootstrap'
import { FeatureHintInputComponent } from '../feature-hint-input/feature-hint-input.component'
import { AuthService } from '../../services/auth/auth.service'
import { EntityType, threedMode } from 'src/app/types/enums'
import { ClikProjectInfo } from '../../types/clik/ClikProjectInfo'
import { ThreedUtils } from './threed-utils'

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'app-threed',
    templateUrl: './threed.component.html',
    styleUrls: ['./threed.component.less'],
    providers: [],
})
export class ThreedComponent implements OnInit {
    constructor(private $http: HttpClient, private authService: AuthService, private sanitizer: DomSanitizer) {}

    public configuration: any = {}
    public rendererURL: SafeResourceUrl = this.trustURL('')
    public rawRendererURL: string = ''
    public initialized: boolean = false
    public entityType = EntityType
    public threedMode = threedMode
    public mode: threedMode = threedMode.DATA_MANAGER

    public assemblyKey = ''
    public assembly: any = ''

    public currentPart = ''
    public currentPartInput = ''
    public partsList: string[] = []
    public filteredPartsList: any[] = []
    public partFocused = false
    public partInfo: any = {
        preTransform: {
            up: '',
            front: '',
        },
    }
    public subcatInfo: any = {}
    public preTransform: any = []
    public preTransformName: any = ''

    public currentSubcategory = ''
    public currentSubcategoryInput = ''
    public subcategoriesList: any[] = []
    public selectedSubcategories: String[] = []
    public subcatFocused = false
    public filteredSubcategoriesList: any[] = []

    public collapsed: boolean = false
    public transparent: boolean = false

    public activePanelDatum: any = null

    private NOOP_FEATURE = '\nfront'
    private ROUND_PLACE: number = 3 // Default 3
    public formatNumber: Function = ThreedUtils.getNumberFormatter(this.ROUND_PLACE)

    public featurePromise: any
    private featurePromiseStale: boolean = false
    private featureResolveFn: any
    private featureLocked: boolean = true

    public loading: boolean = false
    public partsLoading: boolean = false
    public subcategoriesLoading: boolean = false
    public disablePreTransform: boolean = false

    public dataManagerInfo: ClikProjectInfo = new ClikProjectInfo()

    @ViewChildren('featureInput')
    public featureInputs!: QueryList<FeatureHintInputComponent>
    public featureInput!: FeatureHintInputComponent

    ngOnInit(): void {
        this.getFeatureRoundingPlaces()
        // Kick off the renderer init process
        this.activate()
        // Start by setting the mode
        this.updateMode(threedMode.DATA_MANAGER)
    }

    public ngAfterViewInit(): void {
        // triggers whenever the FeatureHintInputComponents view child(ren) changes
        this.featureInputs.changes.subscribe((comps: QueryList<FeatureHintInputComponent>) => {
            this.featureInput = comps.first
        })
    }

    // We'll use this to trust our renderer URL (provided from the server)
    trustURL(url: string) {
        return this.sanitizer.bypassSecurityTrustResourceUrl(url)
    }

    private renderMessageListener = (ev: MessageEvent<any>) => {
        let vm = this
        vm.rendererMessageReceived(ev, vm)
    }

    activate() {
        let vm = this
        // Listen for events from the 3d iframe
        window.addEventListener('message', this.renderMessageListener)

        this.getConfiguration()
    }

    deactivate() {
        let vm = this
        // Tear down our listener (in case it wasn't done earlier)
        window.removeEventListener('message', this.renderMessageListener)
    }

    // Clean up when the controller gets nuked
    ngOnDestroy() {
        this.deactivate()
    }

    changePart(part: string) {
        this.featureLocked = true
        this.featurePromise = null
        this.currentPart = part
        this.currentPartInput = part
        this.partInfo = {
            preTransform: {
                up: '',
                front: '',
            },
        }
        this.updatePart()
    }

    updatePart() {
        this.getSubcategoriesList()
        if (this.currentPart) {
            // Skip if nothing is selected
            this.getDrawing()
            this.getAssembly()
        }
    }

    updatePartInput() {
        let vm = this
        vm.filteredPartsList = _.filter(vm.partsList, function (x) {
            return x.toLowerCase().includes(vm.currentPartInput.toLowerCase())
        })
    }

    updateSubcat(subcat: string) {
        let vm = this
        vm.currentPart = ''
        vm.currentPartInput = ''
        vm.partInfo = ThreedUtils.createPartInfo()
        vm.currentSubcategory = subcat
        vm.currentSubcategoryInput = subcat
        vm.getSubcatInfo()
        vm.getPartList(vm.currentSubcategory)
    }

    updateSubcatInput() {
        let vm = this
        vm.filteredSubcategoriesList = _.filter(vm.subcategoriesList, function (x) {
            return x.toLowerCase().includes(vm.currentSubcategoryInput.toLowerCase())
        })
    }

    updateMode(mode: threedMode) {
        let vm = this
        // Set Mode
        vm.mode = mode
        vm.reset()
        // Fetch Data
        switch (mode) {
            case threedMode.VIEW:
            case threedMode.DATUM:
            case threedMode.BATCH:
                vm.getPartList()
                break
            case threedMode.SUBCAT:
                vm.getSubcategoriesList()
                break
            case threedMode.DATA_MANAGER:
                break
        }
    }

    reset(full?: boolean) {
        let vm = this
        // Clear values
        vm.currentPart = ''
        vm.currentPartInput = ''
        vm.currentSubcategory = ''
        vm.currentSubcategoryInput = ''
        vm.preTransformName = ''
        vm.preTransform = []
        vm.partInfo = ThreedUtils.createPartInfo()
        vm.clearScene()

        if (full) {
            vm.partsList = []
            vm.filteredPartsList = []
            vm.subcategoriesList = []
            vm.filteredSubcategoriesList = []
        }
    }

    dataManagerChange(info: ClikProjectInfo) {
        let vm = this
        if (null !== info && info.configurationName === 'clikruntime') {
            vm.dataManagerInfo = info
            vm.reset(true)
        }
    }

    /**
     * Renderer message calls
     */

    // Handle messages from the renderer iframe
    rendererMessageReceived(e: any, vm: any) {
        // vm scope of ThreedComponent passed in since the scope here is Window
        let message = e.data

        console.log('[Clik3DWeb] ' + e.data.event + ' event from ' + e.origin)

        if (message.event === 'visualization.ready') {
            // Go ahead and stop listening -- we can ignore the renderer now
            // window.removeEventListener('message', vm.renderMessageListener);

            // Set the ui style (switch this to a settings object someday,
            // instead of a single name string)
            let uiStyle = 'clik-3d-web'

            let iframe = document.getElementById('render-container')
            let iWindow = (<HTMLIFrameElement>iframe).contentWindow

            if (iWindow) {
                // First style message sets the toolbar styles
                iWindow.postMessage(
                    {
                        event: 'ui.style.set',
                        ui_style: uiStyle,
                    },
                    vm.rendererURL.changingThisBreaksApplicationSecurity
                )

                // Send in the assembly
                iWindow.postMessage(
                    {
                        event: 'assembly.load',
                        assembly: vm.assembly,
                    },
                    vm.rawRendererURL
                )
            }
        }

        if (message.event === 'scene.initialized') {
            // Set the ui style (switch this to a settings object someday,
            // instead of a single name string)
            let uiStyle = 'clik-3d-web'

            let iframe = document.getElementById('render-container')
            let iWindow = (<HTMLIFrameElement>iframe).contentWindow

            if (iWindow) {
                // Update style again on initialization to set camera settings
                iWindow.postMessage(
                    {
                        event: 'ui.style.set',
                        ui_style: uiStyle,
                    },
                    vm.rendererURL.changingThisBreaksApplicationSecurity
                )
            }

            // Parse the hints from clik
            _.forEach(vm.partInfo.datumList, function (datum) {
                vm.updateDescription(datum, true)
            })
            vm.updateRenderedFeatures(true)
        }

        if (message.event === 'assembly.initialized') {
            // Grab the assembly key
            vm.assemblyKey = message.key
        }

        if (message.event === 'scene.feature.done') {
            Promise.resolve(vm.featurePromise).then(function () {
                vm.featurePromise = null
                vm.featureResolveFn = null
                if (vm.featurePromiseStale) {
                    vm.featurePromiseStale = false
                    vm.updateRenderedFeatures()
                }
            })

            // Call resolve on the promise
            if (vm.featureResolveFn) {
                vm.featureResolveFn()
            }
        }
    }

    updateRenderedFeatures: Function = _.debounce((unlock?: boolean) => {
        let vm = this

        // First call should come from 'scene.initialized', lock until then
        if (unlock || !vm.featureLocked) {
            vm.featureLocked = false

            if (!vm.partInfo.datumList || vm.partInfo.datumList.length == 0) {
                // Nothing to do
                return
            }

            if (vm.featurePromise) {
                // If there is an existing promise mark it stale
                vm.featurePromiseStale = true
            } else {
                vm.featurePromise = new Promise(function (resolve) {
                    vm.updateRenderedFeaturesImpl()
                    vm.featureResolveFn = resolve
                })
            }
        } else {
            console.warn('updateRenderedFeatures blocked until scene.initialized completed')
        }
    }, 1000)

    updateRenderedFeaturesImpl() {
        let vm = this

        let iframe = document.getElementById('render-container')
        let iWindow = (<HTMLIFrameElement>iframe).contentWindow

        let featureDescriptionMap: any = {}
        _.forEach(vm.partInfo.datumList, function (datum, key: number) {
            datum.descriptionDirty(false)
            datum.transformDirty(false)

            let mapKey = datum.featureId || ''

            let line = '\n'
            if (!featureDescriptionMap[mapKey]) {
                featureDescriptionMap[mapKey] = {
                    description: '',
                    colors: [],
                }
                line = ''
            }

            let saturatedColor = ThreedUtils.saturate(ThreedUtils.colorSet[key].rgb, 0.7)

            _.forEach(saturatedColor, function (val, idx) {
                // Adjustments to add transparency to planes.
                let alpha = 1.0
                // If the parsed state is a plane, or if there is no parsed state and the datum type is a plane.
                // If the parsed state is a plane we want to show it as transparent even if the datum type doesn't match.
                if (datum.getEntityType() === EntityType.PLANE) {
                    alpha = 0.3
                }
                featureDescriptionMap[mapKey].colors.push((val / 255) * (idx === 3 ? alpha : 1))
            })

            if (datum.featureDescriptionOverride) {
                line = line + 'transform ' + JSON.stringify(datum.values)
            } else {
                line = line + datum.featureDescription
            }

            // If the line is empty, populate it with a dummy description to ensure the colors remain synced
            if (line.trim() === '' || datum.hidden) {
                line = vm.NOOP_FEATURE
            }

            featureDescriptionMap[mapKey].description = featureDescriptionMap[mapKey].description + line
        })

        let preTransform = FeatureHintInputComponent.calculatePreTransform(
            ThreedUtils.createVectorFromAxis(vm.partInfo.preTransform.front),
            ThreedUtils.createVectorFromAxis(vm.partInfo.preTransform.up)
        )

        _.forEach(featureDescriptionMap, function (value, key) {
            let colors = value.colors.length > 0 ? '[' + value.colors.join(',') + ']' : ''
            if (iWindow) {
                iWindow.postMessage(
                    {
                        event: 'scene.feature.update',
                        params: {
                            a: vm.assemblyKey,
                            fx: preTransform[0],
                            fy: preTransform[1],
                            fz: preTransform[2],
                            ux: preTransform[3],
                            uy: preTransform[4],
                            uz: preTransform[5],
                            cs: colors,
                            mf: 1000,
                            n: key || '',
                            featureHint: value.description,
                        },
                    },
                    vm.rawRendererURL
                )
            }
        })
    }

    clearScene() {
        let vm = this
        vm.rendererURL = this.trustURL('')
        vm.rawRendererURL = ''
        vm.initialized = false
    }

    // Reprocess description for datum
    updateDescription(datum: any, suppressUpdate?: boolean) {
        if (!this.assemblyKey) {
            // If we don't have an assemblyKey yet then don't try to parse.
            return
        }

        let vm = this
        datum.loading++

        let hint = ''
        switch (vm.mode) {
            case threedMode.DATUM:
            case threedMode.SUBCAT:
                hint = datum.featureDescription
                break
        }

        return FeatureHintInputComponent.parseFeatureHint(
            this.$http,
            hint,
            this.assemblyKey,
            ThreedUtils.createVectorFromAxis(vm.partInfo.preTransform.front),
            ThreedUtils.createVectorFromAxis(vm.partInfo.preTransform.up),
            datum.featureId,
            false,
            vm.featureInput
        ).then(
            function (response: any) {
                datum.state = response.data
                // Update the status
                if (datum.state.parseSuccessful) {
                    if (datum.state.logicalStructure.entityEvaluationDetails === 'OK') {
                        datum.status = 'OK'
                    } else {
                        datum.status = 'WARNING'
                    }
                } else {
                    datum.status = 'ERROR'
                }

                // Update the transform if applicable
                let datumStateType = datum.getDetectedEntityType()
                datum.entityTypeMatch = datumStateType === datum.getDatumEntityType()

                if (datum?.state?.logicalStructure?.entityEvaluationSuccessful) {
                    switch (datumStateType) {
                        case EntityType.TRANSFORM:
                            ThreedUtils.updateDatumTransformFromMatrix(
                                datum,
                                datum.state.logicalStructure.entityData,
                                vm.formatNumber
                            )
                            break
                        case EntityType.PLANE:
                            ThreedUtils.updateDatumTransformFromPlane(
                                datum,
                                datum.state.logicalStructure.entityData,
                                vm.formatNumber,
                                true
                            )
                            break
                        case EntityType.HOLE_PATTERN:
                        case EntityType.ARC_PAIR:
                        case EntityType.SLOT:
                            ThreedUtils.updateDatumTransformFromHolePattern(
                                datum,
                                datum.state.logicalStructure.entityData
                            )
                            break
                        default:
                            ThreedUtils.clearDatumTransform(datum)
                    }
                } else {
                    // Clear out entityData, since it is referenced in the UI and we don't want endless rows of
                    // arc or arc pair data crashing the client. If the entityData is to be used for something, do so before this
                    if (datum?.state?.logicalStructure?.entityData) {
                        datum.state.logicalStructure.entityData = []
                    }
                    ThreedUtils.clearDatumTransform(datum)
                }

                if (!suppressUpdate) {
                    vm.updateRenderedFeatures()
                }

                datum.loading--
                if (datum.loading < 0) {
                    datum.loading = 0
                }
            },
            function (response: any) {
                console.error('Error parsing feature hint...')
                datum.loading--
                if (datum.loading < 0) {
                    datum.loading = 0
                }
            }
        )
    }

    /**
     * API Calls
     */

    // Load the renderer configuration (notably, the target URL)
    getConfiguration() {
        let vm = this
        vm.$http
            .get('/api/renderer/configuration')
            .toPromise()
            .then(
                function (response: any) {
                    vm.handleGetRendererConfigurationSuccess(response.configuration)
                },
                function (response: any) {
                    console.error('Error getting renderer configuration...')
                }
            )
    }

    // Get the rounding precision
    getFeatureRoundingPlaces() {
        let vm = this
        vm.$http
            .get('/api/clik-feature/feature-rounding-places/')
            .toPromise()
            .then(
                function (response: any) {
                    vm.ROUND_PLACE = response
                    vm.formatNumber = ThreedUtils.getNumberFormatter(vm.ROUND_PLACE)
                },
                function (response: any) {
                    console.error('Error getting rounding places...')
                }
            )
    }

    // Fetch the assembly from CLIK for the current object
    getAssembly() {
        let vm = this
        vm.$http
            .get('/api/clik-feature/feature-assembly/' + vm.currentPart)
            .toPromise()
            .then(
                function (response: any) {
                    response = resolveReferences(response)
                    vm.handleGetAssemblySuccess(response.assembleResult)
                },
                function (response: any) {
                    console.error('Error getting assembly...')
                }
            )
    }

    // Fetch the drawing from CLIK for the current object
    getDrawing() {
        let vm = this
        vm.$http
            .get('/api/clik-feature/drawing/' + vm.currentPart)
            .toPromise()
            .then(
                function (response: any) {
                    vm.handleGetDrawingSuccess(response)
                },
                function (response: any) {
                    console.error('Error getting drawing...')
                }
            )
        vm.$http
            .get('/api/clik-feature/subcats-for-part/' + vm.currentPart)
            .toPromise()
            .then(
                function (response: any) {
                    vm.handleGetSubcategoriesForPartSuccess(response)
                },
                function (response: any) {
                    console.error('Error getting subcategories for part...')
                }
            )
    }

    // Fetch the partInfo from CLIK for the current object
    getPartInfo(saveOnSuccess?: boolean) {
        let vm = this
        vm.$http
            .get('/api/clik-feature/feature-info/' + vm.currentPart)
            .toPromise()
            .then(
                function (response: any) {
                    vm.handleGetPartInfoSuccess(response, false, saveOnSuccess || false)
                    vm.loading = false
                },
                function (response: any) {
                    console.error('Error getting part info...')
                    vm.loading = false
                }
            )
    }

    // Fetch the partInfo from CLIK for the current object
    getSubcatInfo() {
        let vm = this
        vm.$http
            .get('/api/clik-feature/subcat-info/' + vm.currentSubcategory)
            .toPromise()
            .then(
                function (response: any) {
                    vm.handleGetSubcatInfoSuccess(response[0])
                },
                function (response: any) {
                    console.error('Error getting part info...')
                }
            )
    }

    // Fetch the partList from CLIK
    getPartList(subcatName?: any) {
        let vm = this
        vm.partsLoading = true
        vm.$http
            .get('/api/clik-feature/parts-list', {
                params: { subcatName: subcatName || '' },
            })
            .toPromise()
            .then(
                function (response: any) {
                    vm.partsLoading = false
                    vm.handleGetPartsListSuccess(response)
                },
                function (response: any) {
                    vm.partsLoading = false
                    console.error('Error getting parts list...')
                }
            )
    }

    // Fetch the partList from CLIK
    getSubcategoriesList() {
        let vm = this
        vm.subcategoriesLoading = true
        vm.$http
            .get('/api/clik-feature/subcategories-list')
            .toPromise()
            .then(
                function (response: any) {
                    vm.subcategoriesLoading = false
                    vm.handleGetSubcategoriesListSuccess(response)
                },
                function (response: any) {
                    vm.subcategoriesLoading = false
                    console.error('Error getting subcategories list...')
                }
            )
    }

    // Generate the datum entries in Clik based on the part and the selected subcats
    generateDatum(regenerate: boolean, datumName?: string) {
        let vm = this
        vm.loading = true
        let url = regenerate ? '/api/clik-feature/regenerate-datum/' : '/api/clik-feature/generate-datum/'
        vm.$http
            .post(url + vm.currentPart, vm.selectedSubcategories, {
                params: { datumName: datumName || '' },
            })
            .toPromise()
            .then(
                function (response: any) {
                    vm.getPartInfo(true)
                    vm.loading = false
                },
                function (response: any) {
                    vm.loading = false
                    console.error('Error generating datum info...', response)
                }
            )
    }

    // Save datum changes
    saveDatum(datum?: any) {
        let vm = this
        // If no specific datum provided, save them all
        let toUpdate = datum ? [datum] : vm.partInfo.datumList

        vm.loading = true

        switch (this.mode) {
            case threedMode.DATUM:
                // Get the updated pre-transform without committing it yet
                let preTransform = vm.updatePreTransform(false)

                let payload = {
                    datum: toUpdate,
                    partName: vm.currentPart,
                    transform: preTransform,
                }

                vm.$http
                    .put('/api/clik-feature/update-features-with-pre-transform', payload)
                    .toPromise()
                    .then(
                        function (response: any) {
                            vm.getPartInfo()
                        },
                        function (response: any) {
                            console.error('Error saving datum...', response)
                            vm.loading = false
                        }
                    )
                break
            case threedMode.SUBCAT:
                // Make a map from the template values (list of named strings)
                let templateMap: any = {}
                _.forEach(vm.subcatInfo.featureDescriptionTemplateKeys, function (item, idx) {
                    templateMap[item] = vm.subcatInfo.featureDescriptionTemplateValues[idx]
                })
                // Update values in map from datum descriptions
                _.forEach(toUpdate, function (datum) {
                    templateMap[datum.datum.name] = datum.featureDescription
                })
                // Convert map back to list of named strings
                vm.subcatInfo.featureDescriptionTemplateKeys = []
                vm.subcatInfo.featureDescriptionTemplateValues = []
                _.forEach(templateMap, function (value, key) {
                    vm.subcatInfo.featureDescriptionTemplateKeys.push(key)
                    vm.subcatInfo.featureDescriptionTemplateValues.push(value)
                })

                vm.$http
                    .put('/api/clik-feature/update-templates', [vm.subcatInfo])
                    .toPromise()
                    .then(
                        function (response: any) {
                            vm.getPartInfo()
                        },
                        function (response: any) {
                            console.error('Error saving datum...', response)
                            vm.loading = false
                        }
                    )
                break
        }
    }

    // Save datum changes
    savePreTransform(transform: any) {
        let vm = this
        vm.$http
            .put('/api/clik-feature/update-pre-transform/' + vm.currentPart, transform)
            .toPromise()
            .then(
                function (response: any) {
                    vm.getPartInfo()
                },
                function (response: any) {
                    console.error('Error saving pre-transform...', response)
                }
            )
    }

    /**
     * Success handlers
     */

    handleGetAssemblySuccess(assembly: any) {
        let vm = this
        this.assembly = assembly

        if (this.mode !== threedMode.VIEW) {
            this.getPartInfo()
        }

        // Now that we have the assembly, assign the renderer URL to tell the iframe
        // to load the renderer page -- note that the rendererURL variable is assigned
        // to the controller separately from the configuration value on purpose, as
        // otherwise the iframe will load too early
        if (!this.initialized) {
            // If this is the first initialization set the url
            this.rawRendererURL = this.configuration.rendererURL
            this.rendererURL = this.trustURL(this.configuration.rendererURL)
            this.initialized = true
        } else {
            // Otherwise just post the load message
            let iframe = document.getElementById('render-container')
            let iWindow = (<HTMLIFrameElement>iframe).contentWindow

            if (iWindow) {
                // Send in the assembly
                iWindow.postMessage(
                    {
                        event: 'assembly.load',
                        assembly: vm.assembly,
                    },
                    vm.rawRendererURL
                )
            }
        }
    }

    handleGetDrawingSuccess(drawing: any) {
        let vm = this
        let up = ''
        let front = ''

        if (drawing && drawing[0]) {
            let name = drawing[0].drawingTransform.name

            // Make sure the transform name is the right format (e.g. +X-Y)
            let matchesPattern = name.match(/([+\-][X-Z]){2}/g)
            if (matchesPattern) {
                up = name.slice(0, 2)
                front = name.slice(2, 4)
            }
            let options = ThreedUtils.generatePreTransformAxesOptions('', vm.partInfo)
            vm.disablePreTransform = !matchesPattern || !(options.includes(up) && options.includes(front))

            vm.preTransformName = drawing[0].drawingTransform.name
            vm.preTransform = drawing[0].drawingTransform.values
        }
        vm.partInfo.preTransform = {
            up: up,
            front: front,
        }
    }

    handleGetSubcategoriesForPartSuccess(subcategories: any) {
        let vm = this
        vm.selectedSubcategories = _.map(subcategories, 'name')
    }

    handleGetPartInfoSuccess(partInfo: any, appending: boolean, save: boolean) {
        let vm = this

        partInfo = resolveReferences(partInfo, true)

        if (!this.partInfo) {
            this.partInfo = ThreedUtils.createPartInfo()
        }

        this.partInfo.name = vm.currentPart
        if (!this.partInfo.datumList || !appending) {
            this.partInfo.datumList = []
        }
        this.partInfo.datumList = this.partInfo.datumList.concat(
            _.map(
                _.filter(partInfo, function (datum) {
                    // If we are in subcat mode, filter the datum list down based on the selected subcat
                    return (
                        vm.mode !== threedMode.SUBCAT ||
                        _.some(datum.componentSubcategories, function (subcat) {
                            return subcat.name === vm.currentSubcategory
                        })
                    )
                }),
                function (part) {
                    let datum = ThreedUtils.createDatum(vm.updateRenderedFeatures)
                    datum.name = part.name
                    datum.displayName = part.displayName
                    datum.featureDescriptionOverride = part.featureDescriptionOverride
                    datum.featureDescription = part.featureDescriptionOverride ? '' : part.featureDescription
                    datum.featureId = part.featureId
                    datum.datum = part.datum
                    datum.componentSubcategories = part.componentSubcategories

                    // Auto-populate the
                    if (vm.mode === threedMode.SUBCAT) {
                        datum.componentSubcategories = [vm.subcatInfo]

                        // Find the matching template from the subcat
                        let idx = -1
                        _.forEach(vm.subcatInfo.featureDescriptionTemplateKeys, function (key, i) {
                            if (datum.datum.name === key) {
                                idx = parseInt(i)
                            }
                        })

                        // If the template has a value, use that. Otherwise fall back on the datum value
                        if (idx > -1) {
                            datum.featureDescription = vm.subcatInfo.featureDescriptionTemplateValues[idx]
                        } else if (datum.featureDescription) {
                            datum.descriptionDirty(true)
                        }
                    }

                    if (datum.getDatumEntityType() === EntityType.TRANSFORM && part.values.length === 16) {
                        ThreedUtils.updateDatumTransformFromMatrix(datum, part.values, vm.formatNumber)
                    }
                    if (datum.getDatumEntityType() === EntityType.PLANE && part.values.length === 4) {
                        ThreedUtils.updateDatumTransformFromPlane(datum, part.values, vm.formatNumber)
                    }

                    return datum
                }
            )
        )

        this.partInfo.datumList = _.sortBy(this.partInfo.datumList, 'name')

        if (this.partInfo.datumList.length > ThreedUtils.colorSet.length) {
            let diff = this.partInfo.datumList.length - ThreedUtils.colorSet.length
            for (let i = 0; i < diff; i++) {
                ThreedUtils.generateColor()
            }
        }

        if (!appending) {
            Promise.all(
                _.map(vm.partInfo.datumList, function (datum) {
                    return vm.updateDescription(datum, true)
                })
            ).then(function () {
                if (save) {
                    vm.saveDatum()
                }
            })
            vm.updateRenderedFeatures()
        }
    }

    handleGetSubcatInfoSuccess(subcatInfo: any) {
        let vm = this
        vm.subcatInfo = resolveReferences(subcatInfo, true)
    }

    handleGetPartsListSuccess(partsList: any) {
        let vm = this
        vm.partsList = _.sortBy(partsList)
        vm.filteredPartsList = _.sortBy(partsList)
    }

    handleGetSubcategoriesListSuccess(subcategoriesList: any) {
        let vm = this
        vm.subcategoriesList = _.sortBy(subcategoriesList)
        vm.filteredSubcategoriesList = _.sortBy(subcategoriesList)
    }

    handleGetRendererConfigurationSuccess(configuration: any) {
        let vm = this
        vm.configuration = configuration

        this.updatePart()
    }

    public onPanelChange($event: NgbPanelChangeEvent, vm: any) {}

    anySaveDirty() {
        return _.some(this.partInfo.datumList, function (d) {
            return d.saveDirty()
        })
    }

    updatePreTransform(commit: boolean = false) {
        let vm = this

        // If both components are not set just return
        if (vm.partInfo.preTransform.up === '' || vm.partInfo.preTransform.front === '') {
            return
        }

        // Compute transformation to align axes
        let up = ThreedUtils.createVectorFromAxis(vm.partInfo.preTransform.up)
        let front = ThreedUtils.createVectorFromAxis(vm.partInfo.preTransform.front)
        let transform: any[] = []
        let cross = math.cross(up, front) as number[]
        transform.push(up)
        transform.push(front)
        transform.push(cross)

        let defaultCoordinates = math.transpose([
            [0, 0, 1],
            [1, 0, 0],
            [0, 1, 0],
        ])

        transform = math.multiply(defaultCoordinates, transform)
        transform = MatrixFunctions.format(transform)

        vm.preTransformName = vm.partInfo.preTransform.up + vm.partInfo.preTransform.front
        vm.preTransform = transform

        _.forEach(vm.assembly.assembly.subAssemblies, function (sub) {
            if (sub.name === vm.currentPart) {
                sub.transform.values = transform
            }
        })

        let drawingTransform = {
            name: vm.partInfo.preTransform.up + vm.partInfo.preTransform.front, // Concatenation of axes (e.g. +Z+X)
            description:
                'UP: ' +
                vm.partInfo.preTransform.up +
                ', FRONT: ' +
                vm.partInfo.preTransform.front +
                ' [Generated by ClikAI]',
            values: transform,
        }

        // Only save in datum mode if commit specified
        if (commit && vm.mode === threedMode.DATUM) {
            // Save
            vm.savePreTransform(drawingTransform)
        }

        let iframe = document.getElementById('render-container')
        let iWindow = (<HTMLIFrameElement>iframe).contentWindow

        if (iWindow) {
            // Send in the assembly
            iWindow.postMessage(
                {
                    event: 'assembly.update',
                    assembly: vm.assembly,
                },
                vm.rawRendererURL
            )
        }

        return drawingTransform
    }

    toggleTransparency() {
        let vm = this

        vm.transparent = !vm.transparent

        _.forEach(vm.assembly.assembly.subAssemblies, function (sub) {
            if (sub.name === vm.currentPart) {
                sub.baseColor.r = 0.5
                sub.baseColor.g = 0.5
                sub.baseColor.b = 0.5
                sub.baseColor.a = 0.75
                sub.material.name = vm.transparent ? '#0,1,0,0,0.05,0' : 'predefined'
            }
        })

        let iframe = document.getElementById('render-container')
        let iWindow = (<HTMLIFrameElement>iframe).contentWindow

        if (iWindow) {
            // Send in the assembly
            iWindow.postMessage(
                {
                    event: 'assembly.update',
                    assembly: vm.assembly,
                },
                vm.rawRendererURL
            )
        }
    }

    /**
     * Helper functions
     */

    // Add subcat to selected list
    updateSubcategories(subcat: String) {
        this.selectedSubcategories.push(subcat)
        this.currentSubcategory = ''
    }

    // Remove subcat from selected list
    removeSubcategory(idx: number) {
        this.selectedSubcategories.splice(idx, 1)
    }

    public readonly ThreedUtils = ThreedUtils
}
